import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'

import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Button } from '@quebon/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@quebon/ui/components/field'
import { Input } from '@quebon/ui/components/input'
import { Spinner } from '@quebon/ui/components/spinner'

import { handleApiError } from '@/api/client'
import {
  CUMULATIVE_LEAD_RANGE,
  PRINT_ORDER_LEAD_RANGE,
  progressAlertApi,
  progressAlertQueryKeys,
  type ProgressAlertSettings,
} from '@/api/progressAlert'

interface Props {
  onClose: () => void
}

/** 정수 입력 검증. 범위 밖이거나 정수가 아니면 null. */
export function parseLeadLessons(value: string, range: { min: number; max: number }): number | null {
  if (!/^\d+$/.test(value.trim())) return null
  const parsed = Number(value.trim())
  return parsed >= range.min && parsed <= range.max ? parsed : null
}

const KST_DATE_TIME = new Intl.DateTimeFormat('ko-KR', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

export function formatSettingsUpdatedAt(value: string | null | undefined): string | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : KST_DATE_TIME.format(date)
}

/**
 * 진도 알림 설정 — 전체 코스 공통 기준 2개(교재 발주 / 누적테스트 준비 선행 레슨 수).
 * 권한은 교재 관리와 같은 CONTENTS. 한시 교재 진도 현황판이 없어져도 유지된다.
 */
export function ProgressAlertSettingsDialog({ onClose }: Props) {
  const settingsQuery = useQuery({
    queryKey: progressAlertQueryKeys.settings,
    queryFn: progressAlertApi.getSettings,
  })

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>진도 알림 설정</DialogTitle>
          <DialogDescription>
            매일 오전 9시(한국 시간) Teams 깨봉서비스 › 러닝센터 채널로 보내는 교재 발주·누적테스트 준비 알림의 기준입니다.
            전체 센터·전체 코스에 같이 적용되며, 변경은 다음 오전 9시 발송부터 반영됩니다.
          </DialogDescription>
        </DialogHeader>
        {settingsQuery.isLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : settingsQuery.isError || !settingsQuery.data ? (
          <Alert variant="destructive">
            <AlertTriangle />
            <AlertTitle>설정을 불러오지 못했습니다</AlertTitle>
            <AlertDescription>{handleApiError(settingsQuery.error)}</AlertDescription>
          </Alert>
        ) : (
          <ProgressAlertSettingsForm settings={settingsQuery.data} onClose={onClose} />
        )}
      </DialogContent>
    </Dialog>
  )
}

function ProgressAlertSettingsForm({ settings, onClose }: { settings: ProgressAlertSettings; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [printOrderLead, setPrintOrderLead] = useState(String(settings.printOrderLeadLessons))
  const [cumulativeLead, setCumulativeLead] = useState(String(settings.cumulativeLeadLessons))
  const [submitted, setSubmitted] = useState(false)

  const parsedPrintOrder = parseLeadLessons(printOrderLead, PRINT_ORDER_LEAD_RANGE)
  const parsedCumulative = parseLeadLessons(cumulativeLead, CUMULATIVE_LEAD_RANGE)
  const printOrderInvalid = submitted && parsedPrintOrder == null
  const cumulativeInvalid = submitted && parsedCumulative == null
  const updatedAt = formatSettingsUpdatedAt(settings.updatedAt)

  const mutation = useMutation({
    mutationFn: progressAlertApi.updateSettings,
    onSuccess: (saved) => {
      queryClient.setQueryData(progressAlertQueryKeys.settings, saved)
      toast.success('진도 알림 설정을 저장했습니다. 다음 오전 9시 발송부터 적용됩니다.')
      onClose()
    },
    onError: (error) => toast.error(handleApiError(error)),
  })

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitted(true)
    if (parsedPrintOrder == null || parsedCumulative == null) return
    mutation.mutate({ printOrderLeadLessons: parsedPrintOrder, cumulativeLeadLessons: parsedCumulative })
  }

  return (
    <form className="contents" onSubmit={submit}>
      {!settings.deliveryConfigured ? (
        <Alert>
          <AlertTriangle />
          <AlertTitle>이 환경은 Teams 발송 경로가 설정되지 않았습니다</AlertTitle>
          <AlertDescription>기준은 저장되지만 알림은 발송되지 않습니다.</AlertDescription>
        </Alert>
      ) : null}
      <FieldGroup>
        <Field data-invalid={printOrderInvalid || undefined}>
          <FieldLabel htmlFor="progress-alert-print-order-lead">교재 발주 선행 레슨 수</FieldLabel>
          <Input
            id="progress-alert-print-order-lead"
            type="number"
            inputMode="numeric"
            min={PRINT_ORDER_LEAD_RANGE.min}
            max={PRINT_ORDER_LEAD_RANGE.max}
            value={printOrderLead}
            disabled={mutation.isPending}
            aria-invalid={printOrderInvalid || undefined}
            onChange={(event) => setPrintOrderLead(event.target.value)}
          />
          <FieldDescription>
            선두 원생이 현재 교재의 마지막 레슨보다 이만큼 앞선 레슨을 시작하면, 다음 권 연결을 두 번 따라간 교재가 미발주일 때 알립니다.
            1이면 12레슨 교재의 11레슨 시작이 기준입니다. 체크업북·워크북 각각 판단합니다.
          </FieldDescription>
          {printOrderInvalid ? <FieldError>{PRINT_ORDER_LEAD_RANGE.min}~{PRINT_ORDER_LEAD_RANGE.max} 사이의 정수를 입력하세요.</FieldError> : null}
        </Field>
        <Field data-invalid={cumulativeInvalid || undefined}>
          <FieldLabel htmlFor="progress-alert-cumulative-lead">누적테스트 준비 선행 레슨 수</FieldLabel>
          <Input
            id="progress-alert-cumulative-lead"
            type="number"
            inputMode="numeric"
            min={CUMULATIVE_LEAD_RANGE.min}
            max={CUMULATIVE_LEAD_RANGE.max}
            value={cumulativeLead}
            disabled={mutation.isPending}
            aria-invalid={cumulativeInvalid || undefined}
            onChange={(event) => setCumulativeLead(event.target.value)}
          />
          <FieldDescription>
            누적테스트 배치 위치에서 일반 진도 레슨만 거슬러 센 수입니다(기준 레슨 포함, 챕터 테스트·복습·특강 제외).
            7이면 L1~L7 다음 시험의 L1 시작이 기준입니다. 같은 시험 범위는 전체 센터 통틀어 한 번만 알립니다.
          </FieldDescription>
          {cumulativeInvalid ? <FieldError>{CUMULATIVE_LEAD_RANGE.min}~{CUMULATIVE_LEAD_RANGE.max} 사이의 정수를 입력하세요.</FieldError> : null}
        </Field>
      </FieldGroup>
      {updatedAt ? <p className="text-xs text-muted-foreground">마지막 변경: {updatedAt}</p> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={mutation.isPending}>취소</Button>
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? <Spinner /> : null}
          저장
        </Button>
      </DialogFooter>
    </form>
  )
}
